// DialogBase.tsx — Shared wrapper for all dialogs
// Provides: dialog-box, dialog-header (title + X), dialog-footer (OK + Cancel)
// Keyboard nav: Escape = cancel/close, Enter = confirm (when not in textarea / form field with own handler)

import { useStore } from '../store/useStore';
import { useEffect, useRef, type ReactNode } from 'react';

interface Props {
  title: string;
  onOk?: () => void;
  okLabel?: string;
  cancelLabel?: string;
  hideFooter?: boolean;
  children: ReactNode;
  width?: number;
}

export default function DialogBase({ title, onOk, okLabel = 'OK', cancelLabel = 'Cancel', hideFooter, children, width }: Props) {
  const close = useStore(s => s.closeDialog);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
        return;
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        const target = e.target as HTMLElement | null;
        const tag = target?.tagName;
        // Don't hijack Enter inside textareas or buttons (let them work as normal)
        if (tag === 'TEXTAREA' || tag === 'BUTTON') return;
        // Only fire OK if there's a confirm action and dialog has a footer
        if (!hideFooter && onOk) {
          e.preventDefault();
          onOk();
          close();
        }
      }
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [close, onOk, hideFooter]);

  return (
    <div className="dialog-box" ref={boxRef} style={width ? { width } : undefined} role="dialog" aria-label={title} aria-modal="true">
      <div className="dialog-header">
        <span className="dialog-title">{title}</span>
        <button className="dialog-close" onClick={close} aria-label="Close">✕</button>
      </div>

      <div className="dialog-body">
        {children}
      </div>

      {!hideFooter && (
        <div className="dialog-footer">
          <button className="dialog-btn primary" onClick={() => { onOk?.(); close(); }}>
            {okLabel}
          </button>
          <button className="dialog-btn" onClick={close}>
            {cancelLabel}
          </button>
        </div>
      )}
    </div>
  );
}
